import React from 'react';
import { FileTextIcon, TruckIcon, CalendarIcon, BuildingIcon, MapPinIcon, DollarSignIcon, UserIcon } from 'lucide-react';
import { PurchaseOrder } from './ReceiptingView';
import { formatCurrency } from '../utils/currencyFormatter';
import { StatusBadge } from '../StatusBadge';
interface ReceiptHeaderProps {
  purchaseOrder: PurchaseOrder;
  locale?: string;
}
export function ReceiptHeader({
  purchaseOrder,
  locale = 'en-US'
}: ReceiptHeaderProps) {
  // Get status badge type based on PO status
  const getStatusBadgeType = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'confirmed':
        return 'success';
      case 'pending approval':
      case 'pending confirmation':
        return 'pending';
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-11969">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-11970">
        <div className="flex justify-between items-center" data-id="element-11971">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-11972">
            <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-11973" />
            Purchase Order Details
          </h3>
          <StatusBadge status={getStatusBadgeType(purchaseOrder.status)} text={purchaseOrder.status} size="md" data-id="element-11974" />
        </div>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-11975">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-11976">
          <div data-id="element-11977">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-11978">
              Purchase Order Information
            </h4>
            <div className="space-y-3" data-id="element-11979">
              <div className="flex items-start" data-id="element-11980">
                <div className="flex-shrink-0" data-id="element-11981">
                  <FileTextIcon className="h-5 w-5 text-gray-400" data-id="element-11982" />
                </div>
                <div className="ml-3" data-id="element-11983">
                  <p className="text-sm font-medium text-gray-900" data-id="element-11984">
                    {purchaseOrder.id}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-11985">{purchaseOrder.name}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-11986">
                <div className="flex-shrink-0" data-id="element-11987">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-11988" />
                </div>
                <div className="ml-3" data-id="element-11989">
                  <p className="text-sm font-medium text-gray-900" data-id="element-11990">
                    Order Date
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-11991">{purchaseOrder.date}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-11992">
                <div className="flex-shrink-0" data-id="element-11993">
                  <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-11994" />
                </div>
                <div className="ml-3" data-id="element-11995">
                  <p className="text-sm font-medium text-gray-900" data-id="element-11996">
                    Total Value
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-11997">
                    {formatCurrency(purchaseOrder.total, purchaseOrder.currency, locale)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-11998">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-11999">
              Vendor Information
            </h4>
            <div className="space-y-3" data-id="element-12000">
              <div className="flex items-start" data-id="element-12001">
                <div className="flex-shrink-0" data-id="element-12002">
                  <UserIcon className="h-5 w-5 text-gray-400" data-id="element-12003" />
                </div>
                <div className="ml-3" data-id="element-12004">
                  <p className="text-sm font-medium text-gray-900" data-id="element-12005">Vendor</p>
                  <p className="text-sm text-gray-500" data-id="element-12006">
                    {purchaseOrder.vendor}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-12007">
                <div className="flex-shrink-0" data-id="element-12008">
                  <TruckIcon className="h-5 w-5 text-gray-400" data-id="element-12009" />
                </div>
                <div className="ml-3" data-id="element-12010">
                  <p className="text-sm font-medium text-gray-900" data-id="element-12011">
                    Delivery Status
                  </p>
                  <div className="mt-1" data-id="element-12012">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-12013">
                      Partially Delivered
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-12014">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12015">
              Shipping Information
            </h4>
            <div className="space-y-3" data-id="element-12016">
              <div className="flex items-start" data-id="element-12017">
                <div className="flex-shrink-0" data-id="element-12018">
                  <BuildingIcon className="h-5 w-5 text-gray-400" data-id="element-12019" />
                </div>
                <div className="ml-3" data-id="element-12020">
                  <p className="text-sm font-medium text-gray-900" data-id="element-12021">Entity</p>
                  <p className="text-sm text-gray-500" data-id="element-12022">
                    {purchaseOrder.entity}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-12023">
                <div className="flex-shrink-0" data-id="element-12024">
                  <MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-12025" />
                </div>
                <div className="ml-3" data-id="element-12026">
                  <p className="text-sm font-medium text-gray-900" data-id="element-12027">Site</p>
                  <p className="text-sm text-gray-500" data-id="element-12028">{purchaseOrder.site}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}